import {
  Text,
  Card,
  Box,
  Stack,
  Badge,
  Column,
  Columns,
  Heading,
  IconInfo,
  IconSearch,
  IconVisibility,
  Strong,
  Button,
  Inline,
  Notice,
  TooltipRenderer,
} from 'braid-design-system';

import { useListingUpgradeContractItems } from '../hooks/useListingUpgradeContractItems';

import * as styles from './ListingUpgradeContactItems.css';

interface ListingUpgradeContactItemsProps {
  listingId: string;
  clientId: string;
  contractStartDate: string;
}

export const ListingUpgradeContactItems = ({
  listingId,
  clientId,
  contractStartDate,
}: ListingUpgradeContactItemsProps) => {
  const { items, hasError, loading, handleUpgradeClick } =
    useListingUpgradeContractItems({
      listingId,
      clientId,
      contractStartDate,
    });

  return (
    <Stack space="medium">
      <Columns space="medium" collapseBelow="tablet">
        {items.map((item) => (
          <Column key={item.heading}>
            <Box>
              <Card>
                <Stack space="medium">
                  <Stack space="small">
                    <Box
                      className={
                        item.badgeType
                          ? styles.visibleBadgeSlot
                          : styles.placeholderBadgeSlot
                      }
                    >
                      {item.badgeType ? (
                        <Badge tone="promote">{item.badgeType}</Badge>
                      ) : null}
                    </Box>
                    <Heading level="4">{item.heading}</Heading>
                    <Text>{item.description}</Text>
                  </Stack>

                  <Stack space="small">
                    <Heading level="3" weight="weak">
                      {item.price}
                    </Heading>
                    <Text tone="secondary">+ GST</Text>
                  </Stack>

                  <Stack space="small">
                    <Inline space="xsmall" alignY="center">
                      <Text weight="strong">Estimated performance</Text>
                      <TooltipRenderer
                        tooltip={<Text size="small">{item.tooltipText}</Text>}
                      >
                        {({ triggerProps }) => (
                          <Box
                            {...triggerProps}
                            display="flex"
                            alignItems="center"
                          >
                            <IconInfo tone="formAccent" />
                          </Box>
                        )}
                      </TooltipRenderer>
                    </Inline>
                    <Inline space="xsmall" alignY="center">
                      <IconVisibility tone="formAccent" />
                      <Text>
                        <Strong>{item.viewPerformanceStat}</Strong>{' '}
                        {item.viewPerformanceText}
                      </Text>
                    </Inline>
                    <Inline space="xsmall" alignY="center">
                      <IconSearch tone="formAccent" />
                      <Text>
                        <Strong>{item.searchPerformanceStat}</Strong>{' '}
                        {item.searchPerformanceText}
                      </Text>
                    </Inline>
                  </Stack>

                  <Button
                    tone="formAccent"
                    loading={loading}
                    aria-label={`Upgrade to ${item.heading}`}
                    onClick={() => handleUpgradeClick(item.productId)}
                  >
                    Upgrade
                  </Button>
                </Stack>
              </Card>
            </Box>
          </Column>
        ))}
      </Columns>
      {hasError && (
        <Notice tone="critical">
          <Text>
            Something went wrong sending your upgrade request. Please try again.
          </Text>
        </Notice>
      )}
    </Stack>
  );
};
